import React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { ClickAwayListener, IconButton, InputAdornment, Paper, Popper, TextField } from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import { parse, format, isValid } from 'date-fns';
import { warningColor } from '../project/project.type';
import { maxAllowedDate, minAllowedDate } from '../../common/constants/general';
import { getUserLocale } from '../../common/utils/common.util';

interface DynamicDatePickerProps {
  fieldName: string;
  label?: string;
  value?: string | Date | null;
  onChange: (field: string, value: string | null) => void;
  hasError?: boolean;
  errorMessage?: string;
  textFieldProps?: Record<string, any>;
  referenceDate?: Date;
  dateFormat?: string;
  required?: boolean;
}

// Formats the user is allowed to type. Separators are normalized to "/" before matching,
// so "12-05-2024" and "12.05.2024" are accepted too.
const TYPED_FORMATS = ['MM/dd/yyyy', 'M/d/yyyy', 'MM/dd/yy', 'M/d/yy', 'yyyy/MM/dd'];
const DIGIT_ONLY_FORMATS: Record<number, string> = { 6: 'MMddyy', 8: 'MMddyyyy' };

export const INVALID_DATE_MESSAGE = 'Invalid date. Use MM/DD/YY or MM/DD/YYYY';

/**
 * Parses free-form typed text into a Date. Returns null when the text is not a valid date.
 * Each candidate format is round-tripped through `format` so that overflow values
 * (e.g. "02/30/25") are rejected instead of silently rolling over to the next month.
 */
export const parseTypedDate = (raw: string): Date | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const digitsOnly = /^\d+$/.test(trimmed) ? DIGIT_ONLY_FORMATS[trimmed.length] : undefined;
  if (digitsOnly) {
    const parsed = parse(trimmed, digitsOnly, new Date());
    return isValid(parsed) && format(parsed, digitsOnly) === trimmed ? parsed : null;
  }

  const normalized = trimmed.replace(/[.\-\s]+/g, '/');
  for (const fmt of TYPED_FORMATS) {
    const parsed = parse(normalized, fmt, new Date());
    if (isValid(parsed) && format(parsed, fmt) === normalized) return parsed;
  }

  return null;
};

const DynamicDatePicker: React.FC<DynamicDatePickerProps> = ({
  fieldName,
  label,
  value,
  onChange,
  hasError = false,
  errorMessage = '',
  textFieldProps = {},
  referenceDate,
  dateFormat = 'MM/dd/yy',
  required = false,
}) => {
  const getDateValue = (input: string | Date | null): Date | null => {
    if (!input) return null;

    if (typeof input === 'string') {
      try {
        const datePart = input.split('T')[0];

        // Ensure the extracted date part is in the correct format (yyyy-MM-dd)
        if (datePart.length < 10 || !/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
          return null;
        }

        // parse as local midnight to avoid UTC-offset day shift
        const parsedDate = parse(datePart, 'yyyy-MM-dd', new Date());

        if (isValid(parsedDate)) {
          if (parsedDate < minAllowedDate) return minAllowedDate;
          if (parsedDate > maxAllowedDate) return maxAllowedDate;
          return parsedDate;
        }

        return null;
      } catch (error) {
        console.error(`Invalid date format received: ${input}`);
        return null;
      }
    }

    return isValid(input) ? input : null;
  };

  const {
    onBlur: consumerOnBlur,
    onFocus: consumerOnFocus,
    onKeyDown: consumerOnKeyDown,
    InputProps: consumerInputProps,
    sx: consumerSx,
    disabled,
    ...restTextFieldProps
  } = textFieldProps;

  const dateValue = getDateValue(value ?? null);
  const dateTime = dateValue ? dateValue.getTime() : null;

  // Free-text draft the user types into. Committed to the parent on blur / Enter / calendar pick.
  const [text, setText] = React.useState<string>(dateValue ? format(dateValue, dateFormat) : '');
  const [parseError, setParseError] = React.useState(false);
  const [calendarOpen, setCalendarOpen] = React.useState(false);
  const isFocusedRef = React.useRef(false);
  const anchorRef = React.useRef<HTMLDivElement | null>(null);

  // Mirror external value changes into the text field, but never while the user is mid-edit.
  React.useEffect(() => {
    if (isFocusedRef.current) return;
    setText(dateTime ? format(new Date(dateTime), dateFormat) : '');
    setParseError(false);
  }, [dateTime, dateFormat]);

  // helper to clamp dates within allowed limits
  const clampDate = (date: Date): Date => {
    if (date < minAllowedDate) return minAllowedDate;
    if (date > maxAllowedDate) return maxAllowedDate;
    return date;
  };

  // Live preview so the open calendar follows along as the user types a complete, valid date.
  // Falls back to the committed value while the draft is partial or invalid.
  // Memoised on the text itself — a fresh Date identity every render would make DateCalendar
  // re-navigate its visible month on each keystroke.
  const calendarValue = React.useMemo(() => {
    const previewDate = parseTypedDate(text);
    return previewDate ? clampDate(previewDate) : dateTime ? new Date(dateTime) : null;
  }, [text, dateTime]);

  const commitDate = (date: Date) => {
    const adjusted = clampDate(date);
    setText(format(adjusted, dateFormat));
    setParseError(false);
    onChange(fieldName, format(adjusted, 'yyyy-MM-dd'));
  };

  // Turns whatever is currently typed into a real date (or clears / flags it).
  const commitText = (raw: string) => {
    if (!raw.trim()) {
      setText('');
      setParseError(false);
      onChange(fieldName, null);
      return;
    }

    const parsed = parseTypedDate(raw);
    if (!parsed) {
      // Keep the raw text so the user can correct it; leave the stored value untouched.
      setParseError(true);
      return;
    }

    commitDate(parsed);
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = true;
    // Clicking (or tabbing) into the field reveals the calendar. The popup is a Popper rather
    // than a Popover so focus stays in the input and the user can keep typing over it.
    if (!disabled) setCalendarOpen(true);
    consumerOnFocus?.(event);
  };

  // ClickAwayListener only watches pointer events, so tabbing to another field would otherwise
  // leave this calendar hanging open. Focus moving within the field itself (input <-> calendar
  // icon) is not a departure — the icon's own toggle stays in charge there.
  const closeIfFocusLeftField = (nextFocused: Node | null) => {
    if (!nextFocused || !anchorRef.current?.contains(nextFocused)) {
      setCalendarOpen(false);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = false;
    commitText(event.target.value);
    closeIfFocusLeftField(event.relatedTarget as Node | null);
    consumerOnBlur?.(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      commitText(text);
      setCalendarOpen(false);
    } else if (event.key === 'Escape') {
      setCalendarOpen(false);
    }
    consumerOnKeyDown?.(event);
  };

  // Clicks inside the field (including the calendar icon) are not "away" — the icon's own
  // onClick handles toggling, so closing here too would immediately reopen it.
  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current?.contains(event.target as Node)) return;
    setCalendarOpen(false);
  };

  const handleCalendarChange = (newDate: Date | null) => {
    setCalendarOpen(false);
    if (newDate && isValid(newDate)) commitDate(newDate);
  };

  const showError = hasError || parseError;
  const helperText = hasError ? errorMessage : parseError ? INVALID_DATE_MESSAGE : '';

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getUserLocale()}>
      <TextField
        ref={anchorRef}
        label={label}
        value={text}
        onChange={event => {
          setText(event.target.value);
          if (parseError) setParseError(false);
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        fullWidth
        required={required}
        disabled={disabled}
        error={showError}
        helperText={helperText}
        placeholder='MM/DD/YYYY'
        InputLabelProps={{ shrink: true }}
        {...restTextFieldProps}
        InputProps={{
          ...consumerInputProps,
          endAdornment: (
            <InputAdornment position='end'>
              <IconButton
                aria-label={`open calendar${label ? ` for ${label}` : ''}`}
                edge='end'
                size='small'
                disabled={disabled}
                // Kept out of the tab order: Tab should move from this field to the next one,
                // not to this field's own icon. Keyboard users already get the calendar on focus.
                tabIndex={-1}
                onClick={() => setCalendarOpen(open => !open)}
                onBlur={event => closeIfFocusLeftField(event.relatedTarget as Node | null)}
              >
                <CalendarToday fontSize='small' />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: showError ? `${warningColor} !important` : undefined,
            },
            '&:hover fieldset': {
              borderColor: showError ? `${warningColor} !important` : undefined,
            },
            '&.Mui-focused fieldset': {
              borderColor: showError ? `${warningColor} !important` : 'primary.main',
            },
          },
          '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: `${warningColor} !important`,
          },
          '& .MuiFormLabel-root.Mui-error': {
            color: 'unset !important',
          },
          ...consumerSx,
        }}
      />
      <Popper
        open={calendarOpen && Boolean(anchorRef.current)}
        anchorEl={anchorRef.current}
        placement='bottom-start'
        // Keep the calendar visible when the field sits inside a scrollable table cell.
        modifiers={[{ name: 'preventOverflow', options: { altAxis: true, padding: 8 } }]}
        sx={theme => ({ zIndex: theme.zIndex.modal })}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          {/* Suppressing mousedown's default keeps focus in the input while the calendar is used,
              so closing on blur above can't tear the popup down before a day click registers. */}
          <Paper elevation={8} onMouseDown={event => event.preventDefault()}>
            <DateCalendar
              value={calendarValue}
              onChange={handleCalendarChange}
              referenceDate={referenceDate}
              minDate={minAllowedDate}
              maxDate={maxAllowedDate}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </LocalizationProvider>
  );
};

export default DynamicDatePicker;
